
import { mapState, mapGetters } from 'vuex';
import ReviewContentType from '@/enums/ReviewContentType';
import ReviewSentimentType from '@/enums/ReviewSentimentType';
import AppAiIcon from '@/components/core/AppAiIcon';
import AiIconType from '@/enums/AiIconType';

export default {
  name: 'AppReviewInfoSectionListV3',
  components: {
    AppAiIcon
  },
  props: {
    review: {
      type: Object,
      required: true
    },
    disableCollapse: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      collapsed: true,
      messageRowsCount: -1
    };
  },
  computed: {
    ...mapState('widget', ['widget']),
    ...mapGetters('widget', ['isMobileViewport']),
    ...mapState('reviewSettings', ['reviewSettings']),
    isGradientAiIconType() {
      return this.reviewSettings.ai_icon_type === AiIconType.GRADIENT;
    },
    productOptions() {
      return this.review.product_options.map(o => o.value).join(' ・ ');
    },
    showProductOptions() {
      return (
        this.reviewSettings.show_options &&
        !!this.review.product_options &&
        this.review.product_options.length > 0
      );
    },
    aiSummaryStyleProps() {
      return {
        '--ai-summary-background-color': this.mainColorOpacity8
      };
    },
    mainColorOpacity8() {
      const alpha = Math.round(0.08 * 255)
        .toString(16)
        .padStart(2, '0');

      return `${this.reviewSettings.main_color}${alpha}`;
    },
    expandedMessage() {
      if (
        this.widget.use_review_keyword_filter &&
        this.review.filtered_message_with_highlight
      )
        return this.review.filtered_message_with_highlight.replace(
          /==((\s|.)*?)==/g,
          '<mark>$1</mark>'
        );
      return this.review.filtered_message;
    },
    collapsedMessage() {
      if (!this.collapseAllMessage) return this.expandedMessage;

      let storeName = '';
      if (this.reviewSettings.show_store_name_prefix && this.review.store_name)
        storeName = `[${this.review.store_name}] `;
      return storeName + this.widget.collapsed_title;
    },
    collapseAllMessage() {
      return this.widget.message_initial_rows === 0;
    },
    collapsePartialMessage() {
      return this.widget.message_initial_rows > 0;
    },
    visibleRows() {
      return Math.min(this.messageRowsCount, this.widget.message_initial_rows);
    },
    collapsible() {
      if (this.disableCollapse) return false;
      if (this.review.content_type === ReviewContentType.RESTRICTED)
        return false;
      if (this.collapseAllMessage) return true;
      if (this.collapsePartialMessage) {
        if (
          this.messageRowsCount > this.widget.message_initial_rows ||
          this.review.evaluation_properties?.length > 0
        )
          return true;
      }
      return false;
    },
    showEvaluationTypeOptions() {
      return (
        !!this.review.evaluation_properties &&
        this.review.evaluation_properties.length > 0
      );
    },
    showAiSummary() {
      return (
        this.widget.use_ai_summary &&
        this.review.ai_summary &&
        Object.keys(this.review.ai_summary).length > 0
      );
    },
    positiveAiSummary() {
      return [ReviewSentimentType.STRONG_POSITIVE, ReviewSentimentType.POSITIVE]
        .reduce((acc, sentiment) => {
          const value = this.review.ai_summary[sentiment];
          return value ? acc.concat(value) : acc;
        }, [])
        .join(', ');
    },
    neutralAiSummary() {
      return this.review.ai_summary[ReviewSentimentType.NEUTRAL]?.join(', ');
    },
    negativeAiSummary() {
      return [ReviewSentimentType.STRONG_NEGATIVE, ReviewSentimentType.NEGATIVE]
        .reduce((acc, sentiment) => {
          const value = this.review.ai_summary[sentiment];
          return value ? acc.concat(value) : acc;
        }, [])
        .join(', ');
    },
    aiSummaries() {
      return [
        { sentiment: 'positive', text: this.positiveAiSummary },
        { sentiment: 'neutral', text: this.neutralAiSummary },
        { sentiment: 'negative', text: this.negativeAiSummary }
      ];
    }
  },
  watch: {
    messageRowsCount(newRowsCount, oldRowsCount) {
      const initialRows = this.widget.message_initial_rows;
      if (oldRowsCount > initialRows && newRowsCount > initialRows) return;
      if (oldRowsCount <= initialRows && newRowsCount <= initialRows) return;

      this.collapsed = this.collapsible;
    }
  },
  mounted() {
    if (this.collapsePartialMessage) {
      window.addEventListener('resize', this.calcMessageRowsCount);
      setTimeout(() => {
        this.calcMessageRowsCount();
      });
    }
    this.collapsed = this.collapsible;
  },
  beforeDestroy() {
    if (this.collapsePartialMessage) {
      window.removeEventListener('resize', this.calcMessageRowsCount);
    }
  },
  methods: {
    clickMessageToggle() {
      if (!this.collapsible) return;
      const oldCollapsed = this.collapsed;
      this.collapsed = !oldCollapsed;

      this.$nextTick(() => {
        if (oldCollapsed) {
          this.$refs.exposed_message.focus();
        } else {
          this.$refs.collapsed_message_toggle.$el
            .querySelector('button')
            .focus();
        }
      });
    },
    calcMessageRowsCount() {
      const collapsedHeight = this.$refs.collapsed_message.scrollHeight;
      const exposedHeight = this.$refs.exposed_message.scrollHeight;
      const newMaxHeight =
        collapsedHeight === 0 ? exposedHeight : collapsedHeight;

      const messageLineHeight = parseInt(
        window.getComputedStyle(this.$refs.collapsed_message).lineHeight
      );
      const newMessageRowsCount = newMaxHeight / messageLineHeight;
      if (newMessageRowsCount === this.messageRowsCount) return;

      this.messageRowsCount = newMessageRowsCount;
    }
  }
};
